import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Mail, Smartphone, ArrowRight } from 'lucide-react';
import './ResourceGuide.css';

function MarketingStrategies() {
  const strategies = [
    {
      icon: <Target size={32} />,
      title: 'Local SEO',
      description: 'Dominate local search results when customers look for businesses like yours in your area.',
      tips: ['Optimize your Google Business Profile', 'Build local citations', 'Collect customer reviews']
    },
    {
      icon: <Mail size={32} />,
      title: 'Email Marketing',
      description: 'Stay in touch with customers through email. It\'s 40x more effective than social media.',
      tips: ['Build an email list', 'Send regular updates and offers', 'Segment for personalization']
    },
    {
      icon: <Users size={32} />,
      title: 'Referral Program',
      description: 'Turn happy customers into your best marketers. Word-of-mouth is your most powerful tool.',
      tips: ['Incentivize referrals', 'Make sharing easy', 'Track and reward advocates']
    },
    {
      icon: <Smartphone size={32} />,
      title: 'SMS Marketing',
      description: 'Text messages have a 98% open rate. Perfect for flash sales and time-sensitive offers.',
      tips: ['Build SMS subscriber list', 'Send timely promotions', 'Keep messages short and valuable']
    }
  ];

  return (
    <div className="guide-container">
      <div className="guide-header-section">
        <Link to="/resources" className="back-link">← Back to Resources</Link>
        <h1>Marketing Strategies for 2025</h1>
        <p className="guide-subtitle">Proven tactics to attract customers and grow your revenue</p>
      </div>

      <div className="guide-content">
        <section className="guide-section">
          <h2>The Modern Marketing Playbook</h2>
          <p>
            Today's most successful businesses don't rely on just one marketing channel. They use a mix of digital tactics 
            to reach customers where they are. Here's what actually works in 2025:
          </p>
        </section>

        <section className="guide-section">
          <h2>Core Marketing Strategies</h2>
          <div className="strategies-grid">
            {strategies.map((strategy, index) => (
              <div key={index} className="strategy-card">
                <div className="strategy-icon">{strategy.icon}</div>
                <h3>{strategy.title}</h3>
                <p>{strategy.description}</p>
                <ul className="strategy-tips">
                  {strategy.tips.map((tip, tipIndex) => (
                    <li key={tipIndex}>✓ {tip}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="guide-section">
          <h2>The Customer Journey Approach</h2>
          <div className="journey-steps">
            <div className="journey-step">
              <div className="step-number">1</div>
              <h3>Awareness</h3>
              <p>Get in front of customers searching for what you offer. Use SEO, ads, and social media.</p>
            </div>
            <div className="journey-arrow">→</div>
            <div className="journey-step">
              <div className="step-number">2</div>
              <h3>Consideration</h3>
              <p>Show why you're better. Reviews, photos, and testimonials build trust and credibility.</p>
            </div>
            <div className="journey-arrow">→</div>
            <div className="journey-step">
              <div className="step-number">3</div>
              <h3>Conversion</h3>
              <p>Make buying easy. Clear CTAs, simple checkout, and multiple payment options.</p>
            </div>
            <div className="journey-arrow">→</div>
            <div className="journey-step">
              <div className="step-number">4</div>
              <h3>Retention</h3>
              <p>Reward loyalty. Loyalty programs, personalized offers, and excellent service turn one-time buyers into regulars.</p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Budget Allocation (What Works Best)</h2>
          <div className="budget-breakdown">
            <div className="budget-item">
              <div className="budget-bar" style={{width: '30%'}}></div>
              <p><strong>30%</strong> - Local SEO & Google</p>
            </div>
            <div className="budget-item">
              <div className="budget-bar" style={{width: '25%'}}></div>
              <p><strong>25%</strong> - Email & SMS Marketing</p>
            </div>
            <div className="budget-item">
              <div className="budget-bar" style={{width: '20%'}}></div>
              <p><strong>20%</strong> - Social Media</p>
            </div>
            <div className="budget-item">
              <div className="budget-bar" style={{width: '15%'}}></div>
              <p><strong>15%</strong> - Paid Advertising</p>
            </div>
            <div className="budget-item">
              <div className="budget-bar" style={{width: '10%'}}></div>
              <p><strong>10%</strong> - Content & Reviews</p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Quick Wins This Month</h2>
          <div className="quick-wins">
            <div className="win-card">
              <h3>🎯 Start an Email List</h3>
              <p>Collect customer emails and send a weekly tip or offer. Low effort, high ROI.</p>
            </div>
            <div className="win-card">
              <h3>⭐ Generate Reviews</h3>
              <p>Ask your happiest customers for reviews. Run a small contest if needed.</p>
            </div>
            <div className="win-card">
              <h3>💬 Create an SMS Campaign</h3>
              <p>Text customers about flash sales or new offerings. 98% will see it.</p>
            </div>
          </div>
        </section>

        <section className="guide-cta">
          <h2>Put This Into Action</h2>
          <p>Our platform makes all these strategies easy to execute. See how to manage everything in one place.</p>
          <Link to="/features" className="guide-cta-btn">Explore Our Tools <ArrowRight size={18} /></Link>
        </section>
      </div>

      <div className="guide-navigation">
        <Link to="/resources/seo-guide" className="next-guide">
          Next: SEO for Your Business <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
}

export default MarketingStrategies;
